import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ChefHat } from 'lucide-react';

export const SweetAndSourPork: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Authentic Sweet and Sour Pork Recipe | Chinese Food Maps</title>
        <meta name="description" content="Learn how to make crispy sweet and sour pork with our authentic Chinese recipe. Master the perfect balance of flavors and achieve that restaurant-quality taste." />
        <meta property="og:title" content="Authentic Sweet and Sour Pork Recipe | Chinese Food Maps" />
        <meta property="og:description" content="Learn how to make crispy sweet and sour pork with our authentic Chinese recipe." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://chinesefoodmaps.com/recipes/sweet-and-sour-pork" />
        <meta property="og:image" content="https://cdn-icons-png.flaticon.com/512/2548/2548242.png" />
        <link rel="canonical" href="https://chinesefoodmaps.com/recipes/sweet-and-sour-pork" />
      </Helmet>
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-6 text-red-900">Sweet and Sour Pork Recipe</h1>
        
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="prose max-w-none text-gray-600">
            <p className="text-lg leading-relaxed mb-6">
              Sweet and Sour Pork (糖醋里脊) is a classic Chinese dish featuring crispy fried pork in a tangy, sweet, and sour sauce. This recipe will help you create the perfect balance of flavors.
            </p>

            <h3 className="text-xl font-semibold text-red-800 mb-4">For the Pork:</h3>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>1 lb pork tenderloin, cut into 1-inch cubes</li>
              <li>1 egg</li>
              <li>2 tbsp soy sauce</li>
              <li>1 cup cornstarch</li>
              <li>½ cup flour</li>
              <li>1 tsp white pepper</li>
              <li>Oil for deep frying</li>
            </ul>

            <h3 className="text-xl font-semibold text-red-800 mb-4">For the Sauce:</h3>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>½ cup ketchup</li>
              <li>¼ cup rice vinegar</li>
              <li>¼ cup pineapple juice</li>
              <li>3 tbsp brown sugar</li>
              <li>2 tbsp soy sauce</li>
              <li>1 tbsp cornstarch mixed with 2 tbsp water</li>
            </ul>

            <h3 className="text-xl font-semibold text-red-800 mb-4">For the Vegetables:</h3>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>1 green bell pepper, chunked</li>
              <li>1 red bell pepper, chunked</li>
              <li>1 onion, chunked</li>
              <li>1 cup pineapple chunks</li>
              <li>2 cloves garlic, minced</li>
              <li>1-inch ginger, minced</li>
            </ul>

            <h3 className="text-xl font-semibold text-red-800 mb-4">Instructions:</h3>
            <ol className="list-decimal pl-6 mb-6 space-y-4">
              <li>
                <strong>Marinate Pork:</strong> Mix pork with egg and soy sauce. Let sit for 30 minutes.
              </li>
              <li>
                <strong>Prepare Coating:</strong> Mix cornstarch, flour, and white pepper.
              </li>
              <li>
                <strong>Fry Pork:</strong> Coat pork in flour mixture, deep fry until golden brown.
              </li>
              <li>
                <strong>Make Sauce:</strong> Combine sauce ingredients except cornstarch slurry.
              </li>
              <li>
                <strong>Stir-fry Vegetables:</strong> Cook vegetables until slightly tender.
              </li>
              <li>
                <strong>Combine:</strong> Add sauce and cornstarch slurry, simmer until thickened.
              </li>
              <li>
                <strong>Finish:</strong> Toss fried pork in the sauce just before serving.
              </li>
            </ol>

            <div className="bg-yellow-50 rounded-lg p-6 mt-8">
              <h4 className="text-lg font-semibold text-red-800 mb-2">Pro Tips from Chinese Food Maps:</h4>
              <ul className="list-disc pl-6 space-y-2">
                <li>Double-fry the pork for extra crispiness</li>
                <li>Don't add the pork to the sauce until serving</li>
                <li>Use fresh pineapple for better flavor</li>
                <li>Keep the oil temperature steady at 350°F (175°C)</li>
                <li>Cut all ingredients to similar sizes for even cooking</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};